// client/src/components/watchdog/WatchdogDashboard.jsx
// Dashboard view: pipeline status bar, stats cards, inline config, order table, activity log.

import React, { useState, useCallback } from "react";
import WatchdogStatsCards from "./WatchdogStatsCards";
import WatchdogConfigPanel from "./WatchdogConfigPanel";
import WatchdogOrderTable from "./WatchdogOrderTable";
import WatchdogActivityLog from "./WatchdogActivityLog";
import ConfirmModal from "./ConfirmModal";
import Toast from "./Toast";
import {
    useWatchdogStats,
    useWatchdogOrders,
    useWatchdogActivity,
    useWatchdogConfig,
    useWatchdogActions,
} from "../../hooks/useWatchdog";

const pipelineBarStyle = {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "10px 16px",
    background: "linear-gradient(135deg, #1a1f2e 0%, #151921 100%)",
    borderRadius: "10px",
    border: "1px solid #1e293b",
    marginBottom: "16px",
    fontSize: "12px",
};

const dotStyle = (running) => ({
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: running ? "#22c55e" : "#ef4444",
    boxShadow: running ? "0 0 8px #22c55e" : "0 0 8px #ef4444",
    display: "inline-block",
});

const metricStyle = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: "#94a3b8",
};

const metricValue = {
    fontWeight: 700,
    color: "#e2e8f0",
    fontVariantNumeric: "tabular-nums",
};

const toastContainer = {
    position: "fixed",
    top: "16px",
    right: "16px",
    zIndex: 10000,
    maxWidth: "420px",
    width: "100%",
};

export default function WatchdogDashboard() {
    const { stats, processor, lastUpdated, error: statsError } = useWatchdogStats();
    const {
        orders, total, pages, page, setPage,
        filters, setFilters, loading: ordersLoading, error: ordersError, refresh: refreshOrders,
    } = useWatchdogOrders();
    const [activityFilter, setActivityFilter] = useState("all");
    const { activity, loading: activityLoading } = useWatchdogActivity(30, activityFilter);
    const { config, saving, save, saveError, clearSaveError } = useWatchdogConfig();
    const {
        execute, dismiss, revalidate, clearCompleted, bulkDismiss, actionLoading,
        actionError, actionResult, clearActionError, clearActionResult,
    } = useWatchdogActions(refreshOrders);

    // ─── Confirmation modal state ────────────────────────────────────────────
    const [confirm, setConfirm] = useState(null);
    // confirm = { title, message, confirmLabel, confirmColor, action: () => ... }

    const handleExecute = useCallback((orderId) => {
        const order = orders.find(o => o.id === orderId);
        const orderNum = order?.order_number || orderId;
        const lineItems = order?.line_item_validation || [];
        const cancelCount = lineItems.filter(l => l.should_cancel).length;

        setConfirm({
            title: "Cancel Line Items",
            message: `This will cancel ${cancelCount || "mismatched"} line item(s) on order ${orderNum} in ShipHero, tag the order, and add a history entry. This cannot be undone.`,
            confirmLabel: "Cancel Items",
            confirmColor: "#ef4444",
            action: () => { execute(orderId); setConfirm(null); },
        });
    }, [orders, execute]);

    const handleClearCompleted = useCallback(() => {
        const completedCount =
            (stats?.fulfilled || 0) +
            (stats?.action_taken || 0) +
            (stats?.resolved_by_shiphero || 0) +
            (stats?.validated_ok || 0) +
            (stats?.dismissed || 0) +
            (stats?.cancelled_in_shopify || 0);

        setConfirm({
            title: "Clear Completed Orders",
            message: `This will archive ${completedCount || "all"} completed order(s) — dismissed, fulfilled, cancelled, action taken, resolved, and validated OK. They are hidden from the list but kept in the database as the audit trail of actions taken (see the Archived filter). Active and failed orders are not affected.`,
            confirmLabel: "Clear Completed",
            confirmColor: "#f59e0b",
            action: () => { clearCompleted(); setConfirm(null); },
        });
    }, [stats, clearCompleted]);

    const handleModeChange = useCallback((newMode) => {
        if (newMode === "auto_execute") {
            setConfirm({
                title: "Enable Auto Execute",
                message: "In Auto Execute mode, the Watchdog will automatically cancel mismatched line items in ShipHero without manual intervention. This is a destructive operation that cannot be undone. Are you sure?",
                confirmLabel: "Enable Auto Execute",
                confirmColor: "#ef4444",
                action: async () => {
                    await save({ ...config, watchdog_mode: "auto_execute" });
                    setConfirm(null);
                },
            });
        } else {
            save({ ...config, watchdog_mode: newMode });
        }
    }, [config, save]);

    // ─── Toasts ──────────────────────────────────────────────────────────────
    const toasts = [];
    if (actionError) {
        toasts.push({ key: "actionError", type: "error", message: `${actionError.action} failed: ${actionError.message}`, onDismiss: clearActionError });
    }
    if (actionResult) {
        toasts.push({ key: "actionResult", type: "success", message: actionResult.message, onDismiss: clearActionResult });
    }
    if (saveError) {
        toasts.push({ key: "saveError", type: "error", message: `Config save failed: ${saveError}`, onDismiss: clearSaveError });
    }
    if (statsError) {
        toasts.push({ key: "statsError", type: "warning", message: `Stats: ${statsError}` });
    }
    if (ordersError) {
        toasts.push({ key: "ordersError", type: "warning", message: `Orders: ${ordersError}` });
    }

    return (
        <>
            {/* ─── Toasts (fixed top-right) ──────────────────────────────── */}
            {toasts.length > 0 && (
                <div style={toastContainer}>
                    {toasts.map(t => (
                        <Toast
                            key={t.key}
                            type={t.type}
                            message={t.message}
                            onDismiss={t.onDismiss}
                            duration={t.type === "error" ? 12000 : 8000}
                        />
                    ))}
                </div>
            )}

            {/* ─── Confirmation Modal ────────────────────────────────────── */}
            <ConfirmModal
                open={!!confirm}
                title={confirm?.title}
                message={confirm?.message}
                confirmLabel={confirm?.confirmLabel}
                confirmColor={confirm?.confirmColor}
                onConfirm={confirm?.action}
                onCancel={() => setConfirm(null)}
            />

            {/* ─── Pipeline Status Bar ───────────────────────────────────── */}
            <div style={pipelineBarStyle}>
                <div style={metricStyle}>
                    <span style={dotStyle(processor?.processorRunning)} />
                    <span style={{ color: processor?.processorRunning ? "#22c55e" : "#ef4444", fontWeight: 600 }}>
                        {processor?.processorRunning ? "Processor Running" : "Processor Stopped"}
                    </span>
                </div>
                <div style={{ width: 1, height: 16, background: "#334155" }} />
                <div style={metricStyle}>
                    Debounce Queue: <span style={metricValue}>{processor?.debounceQueueSize ?? 0}</span>
                </div>
                <div style={{ width: 1, height: 16, background: "#334155" }} />
                <div style={metricStyle}>
                    Currently Processing: <span style={metricValue}>{processor?.currentlyProcessing ? "Yes" : "Idle"}</span>
                </div>
                {processor?.debounceOrders?.length > 0 && (
                    <>
                        <div style={{ width: 1, height: 16, background: "#334155" }} />
                        <div style={{ ...metricStyle, flexWrap: "wrap" }}>
                            Debouncing:
                            {processor.debounceOrders.slice(0, 5).map(o => (
                                <span key={o} style={{ color: "#f59e0b", fontWeight: 600, fontSize: "11px" }}>{o}</span>
                            ))}
                            {processor.debounceOrders.length > 5 && (
                                <span style={{ color: "#64748b" }}>+{processor.debounceOrders.length - 5} more</span>
                            )}
                        </div>
                    </>
                )}
                <div style={{ marginLeft: "auto" }}>
                    <button
                        onClick={handleClearCompleted}
                        disabled={actionLoading === "clear-completed"}
                        style={{
                            padding: "5px 14px",
                            background: "transparent",
                            border: "1px solid #f59e0b",
                            borderRadius: "6px",
                            color: "#f59e0b",
                            fontSize: "12px",
                            fontWeight: 600,
                            cursor: actionLoading === "clear-completed" ? "not-allowed" : "pointer",
                            opacity: actionLoading === "clear-completed" ? 0.5 : 1,
                            whiteSpace: "nowrap",
                            transition: "all 0.15s ease",
                        }}
                        onMouseEnter={e => { e.target.style.background = "#f59e0b"; e.target.style.color = "#1a1f2e"; }}
                        onMouseLeave={e => { e.target.style.background = "transparent"; e.target.style.color = "#f59e0b"; }}
                        title="Remove all completed orders (dismissed, fulfilled, cancelled, action taken, resolved, validated OK)"
                    >
                        {actionLoading === "clear-completed" ? "Clearing…" : "🧹 Clear Completed"}
                    </button>
                </div>
            </div>

            <WatchdogStatsCards stats={stats} lastUpdated={lastUpdated} />

            <WatchdogConfigPanel
                config={config}
                saving={saving}
                onSave={save}
                onModeChange={handleModeChange}
                error={saveError}
            />

            <WatchdogOrderTable
                orders={orders}
                total={total}
                pages={pages}
                page={page}
                setPage={setPage}
                filters={filters}
                setFilters={setFilters}
                loading={ordersLoading}
                onExecute={handleExecute}
                onDismiss={dismiss}
                onRevalidate={revalidate}
                onBulkDismiss={bulkDismiss}
                actionLoading={actionLoading}
                actionError={actionError}
            />

            <WatchdogActivityLog
                activity={activity}
                loading={activityLoading}
                lastUpdated={lastUpdated}
                filter={activityFilter}
                onFilterChange={setActivityFilter}
            />
        </>
    );
}
